// Hour-bucket coverage primitives. The schedule grid lets a
// supervisor type custom hours into a cell (e.g. "12-18" — the
// officer is only here from noon to 6pm) on top of the shift's
// own start/end window. Counting that officer as a flat 1.0 for
// the whole shift over-reports coverage: a 06-18 shift with two
// full-day officers + one 12-18 officer is *short* between 6am
// and noon, even though the headcount on paper is 3.
//
// These helpers carve the shift into integer-hour buckets and let
// the coverage loop add per-hour contributions. Reporting the
// MIN bucket count as the day's coverage means a partial-hour
// officer correctly drops the number for the hours they're not
// there, which surfaces the gap on the schedule grid.
//
// Resolution: we work at integer-hour resolution. Notes like
// "06:30-18:00" floor to 6 and 18, which over-counts the half
// hour at the start by ~30 minutes — acceptable since the
// schedule's gaps are typically hour-aligned and the common case
// (whole-hour custom notes) is exact.

// Parse a "HH:MM" / "HHMM" / "HH" string to its integer hour.
// Returns null if it can't be parsed.
export function hourOf(timeStr: string | null | undefined): number | null {
  if (!timeStr) return null;
  const m = timeStr.match(/^(\d{1,2})/);
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n) || n < 0 || n > 23) return null;
  return n;
}

// Parse a cell note like "12-18", "06-18", "14-02", "9-21" into
// a [startHour, endHour] pair (24h, 0..23). Returns null for
// anything that isn't a clean two-integer hour range — letter-
// only codes, junk, etc.
export function parseHourNote(note: string): [number, number] | null {
  // Strip a trailing " HRS" / hr suffix if someone typed one.
  // Accept en-dash, hyphen, or "to" as the separator.
  const trimmed = note.trim().toUpperCase()
    .replace(/\s*HRS?$/, "")
    .replace(/\s+TO\s+/, "-");
  const m = trimmed.match(/^0*(\d{1,2})\s*[-–]\s*0*(\d{1,2})$/);
  if (!m) return null;
  const s = Number(m[1]);
  const e = Number(m[2]);
  if (!Number.isFinite(s) || !Number.isFinite(e)) return null;
  if (s < 0 || s > 23 || e < 0 || e > 23) return null;
  return [s, e];
}

// Return the integer-hour buckets covered by a [startHour, endHour)
// range, wrapping across midnight if needed.
//   bucketsBetween(6, 18) → [6, 7, …, 17]   (12 buckets)
//   bucketsBetween(22, 6) → [22, 23, 0, 1, 2, 3, 4, 5]
//   bucketsBetween(12, 12) → []  (zero-length, treat as no overlap)
// The end hour is exclusive — a shift "06-18" ends AT 18:00, so
// the 18 bucket itself isn't worked.
export function bucketsBetween(startH: number, endH: number): number[] {
  const out: number[] = [];
  if (startH === endH) return out;
  let h = startH % 24;
  // Cap at 24 iterations so a runaway range can't loop forever.
  for (let i = 0; i < 24 && h !== endH; i++) {
    out.push(h);
    h = (h + 1) % 24;
  }
  return out;
}
